import { useToast } from '@viastud/ui/hooks/use-toast'
import { trpc } from '@viastud/ui/lib/trpc'
import { useCallback, useMemo, useState } from 'react'

import { useAuthStore } from '@/store/auth.store'

type SubscriptionStatus =
  'active' | 'suspended' | 'cancelled' | 'expired' | 'pending' | 'past_due' | 'inactive' | 'ended'

export interface StudentSubscriptionInfo {
  status: SubscriptionStatus
  planId: number | null
  endDate?: string | null
  nextBilling?: string | null
  isActive: boolean
  isCancelable: boolean
  isRenewable: boolean
  commitment: boolean
  commitmentEndDate: string | null
}

export function useStudentSubscriptionPresenter() {
  const { user: student } = useAuthStore()
  const { toast } = useToast()

  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [subscribeOpen, setSubscribeOpen] = useState(false)
  const [isSubscribeLoading, setIsSubscribeLoading] = useState(false)
  const [firstSubscriptionPrice, setFirstSubscriptionPrice] = useState(0)

  // Queries
  const {
    data: subscriptionDetails,
    refetch,
    isLoading,
  } = trpc.user.getUserSubscriptionDetails.useQuery()
  const { data: studentPlan, isLoading: studentPlanLoading } =
    trpc.subscriptionPlan.getStudentPlan.useQuery()
  const { data: payments = [], isLoading: paymentsLoading } = trpc.user.getUserPayments.useQuery()
  const { data: invoices = [], isLoading: invoicesLoading } = trpc.user.getUserInvoices.useQuery()
  const { data: promoCodeOffer } = trpc.user.getPromotionalCodeOffer.useQuery()

  // Mutations
  const subscribeMutation = trpc.payment.createSubscriptionIntentForStudent.useMutation({
    onSuccess: (data) => {
      if (data.clientSecret) {
        setClientSecret(data.clientSecret)
        setSubscribeOpen(true)
      }
    },
  })

  const cancelMutation = trpc.payment.cancelSubscriptionForStudent.useMutation({
    onSuccess: () => refetch(),
  })
  const renewMutation = trpc.payment.renewSubscriptionForStudent.useMutation({
    onSuccess: () => refetch(),
  })
  const rePayMutation = trpc.payment.rePaySubscriptionForStudent.useMutation({
    onSuccess: () => refetch(),
  })
  const downloadMutation = trpc.user.downloadInvoice.useMutation({
    onSuccess: (data: { pdfUrl: string }) => window.open(data.pdfUrl, '_blank'),
  })

  // Actions
  const handleSubscribe = useCallback(
    async (commitment: boolean) => {
      try {
        setIsSubscribeLoading(true)
        if (!student) return
        if (!studentPlan) {
          toast({
            title: 'Abonnement indisponible',
            description: 'Le tarif correspondant à votre niveau est indisponible.',
            variant: 'destructive',
          })
          return
        }

        const response = await subscribeMutation.mutateAsync({
          role: 'STUDENT',
          userId: student.id,
          planId: studentPlan.id,
          commitment: commitment,
        })
        if (response.firstSubscriptionPrice) {
          setFirstSubscriptionPrice(response.firstSubscriptionPrice)
        }
      } catch (error) {
        toast({
          title: "Une erreur est survenue lors de la prise d'abonnement",
          description:
            error instanceof Error
              ? error.message
              : 'Une erreur est survenue. Veuillez réessayer ou contacter le support.',
          variant: 'destructive',
        })
      } finally {
        setIsSubscribeLoading(false)
      }
    },
    [student, studentPlan, subscribeMutation, toast]
  )

  const handleCancel = useCallback(async () => {
    try {
      await cancelMutation.mutateAsync()
    } catch (error) {
      console.error('Cancellation failed', error)
    }
  }, [cancelMutation])

  const handleRenew = useCallback(async () => {
    try {
      await renewMutation.mutateAsync()
    } catch (error) {
      console.error('Renewal failed', error)
    }
  }, [renewMutation])

  const handleRePay = useCallback(async () => {
    try {
      const url = await rePayMutation.mutateAsync()
      window.open(url, '_target')
    } catch (error) {
      console.error(error)
      return null
    }
  }, [rePayMutation])

  const handleDownloadInvoice = useCallback(
    (invoiceId: string) => {
      downloadMutation.mutate({ stripeInvoiceId: invoiceId })
    },
    [downloadMutation]
  )

  const studentSubscription: StudentSubscriptionInfo | null = useMemo(() => {
    if (!subscriptionDetails?.subscription) {
      return null
    }

    const sub = subscriptionDetails.subscription

    return {
      status: sub.status,
      planId: sub.planId,
      endDate: sub.endDate ?? null,
      nextBilling: sub.nextBilling ?? null,
      isActive: sub.status === 'active',
      isCancelable: sub.status === 'active',
      isRenewable: sub.status === 'cancelled',
      commitment: sub.commitment,
      commitmentEndDate: sub.commitmentEndDate,
    }
  }, [subscriptionDetails])

  return {
    // Data
    subscription: studentSubscription,
    subscriptionDetails,
    studentPlan,
    payments,
    invoices,
    promotionalCodeOffer: promoCodeOffer?.code ?? undefined,
    firstSubscriptionPrice,

    // Payment flow
    clientSecret,
    subscribeOpen,
    setSubscribeOpen,

    // States
    isSubscribeLoading,
    isLoading: isLoading || paymentsLoading || invoicesLoading || studentPlanLoading,
    isActionLoading:
      subscribeMutation.isPending || cancelMutation.isPending || renewMutation.isPending,
    isDownloading: downloadMutation.isPending,

    // Actions
    handleSubscribe,
    handleCancel,
    handleRenew,
    handleRePay,
    handleDownloadInvoice,
    refetch,
  }
}
